import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIncomingCall } from '../../hooks/useIncomingCall';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { ActiveCall, saveCallPanelPosition } from '../../services/incomingCallAPI';

/**
 * Global incoming-call screen pop. Mounted once in StoreProtectedRoute (like NewOrderWatcher)
 * so it watches every /store/* page.
 *
 * - Entrance matches the locker-order notification (NewOrderWatcher): same Web-Audio chime,
 *   white card + strong shadow, so a call reads as the same kind of event the team knows.
 * - Draggable by the HEADER only (buttons stay clickable), reusing MillCommandDashboard's drag
 *   pattern (pos state + drag ref + document mousemove/mouseup, clamp to viewport).
 * - Position persists PER-USER on the User model (shared POS terminals, so localStorage would
 *   let cashiers overwrite each other) - loaded from the profile the app already fetches, saved
 *   via a PATCH on drag END only.
 *
 * Styling uses inline style objects - Tailwind utilities are NOT applied in this build.
 */
const CARD_W = 340;
const MARGIN = 8;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Copied verbatim from NewOrderWatcher so a new call sounds like a new locker order.
const playChime = () => {
  try {
    const ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    o.type = 'sine';
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.5);
  } catch {
    // No audio context (or user gesture required); skip silently.
  }
};

const IncomingCallWatcher: React.FC = () => {
  const { call, dismiss } = useIncomingCall();
  const { user, setCallPanelPosition } = useStoreAuth();

  // Chime once per new call (call_key changes), matching the locker-order entrance.
  const prevKey = useRef<string | null>(null);
  useEffect(() => {
    const key = call?.call_key ?? null;
    if (key && key !== prevKey.current) playChime();
    prevKey.current = key;
  }, [call?.call_key]);

  const onSavePosition = useCallback(
    (pos: { x: number; y: number }) => {
      setCallPanelPosition(pos);                 // in-session: next call opens here too
      saveCallPanelPosition(pos.x, pos.y).catch(() => {
        /* best-effort persistence; position still holds for this session */
      });
    },
    [setCallPanelPosition]
  );

  if (!call) return null;
  return (
    <IncomingCallPanel
      call={call}
      onDismiss={dismiss}
      savedPosition={user?.call_panel_position ?? null}
      onSavePosition={onSavePosition}
    />
  );
};

const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 8,
  fontSize: 13,
  padding: '3px 0',
};
const label: React.CSSProperties = { color: MUTED, whiteSpace: 'nowrap' };
const linkText: React.CSSProperties = { color: '#047857', cursor: 'pointer', fontWeight: 600 };

interface PanelProps {
  call: ActiveCall;
  onDismiss: () => void;
  savedPosition: { x: number; y: number } | null;
  onSavePosition: (pos: { x: number; y: number }) => void;
}

const IncomingCallPanel: React.FC<PanelProps> = ({
  call,
  onDismiss,
  savedPosition,
  onSavePosition,
}) => {
  const navigate = useNavigate();
  const ctx = call.context;

  const cardRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const dragged = useRef(false);

  const [pos, setPos] = useState(() => {
    const cw = CARD_W;
    const ch = 240; // estimate before the card measures; re-clamped on mount
    if (savedPosition && typeof savedPosition.x === 'number') {
      return {
        x: clamp(savedPosition.x, MARGIN, window.innerWidth - cw - MARGIN),
        y: clamp(savedPosition.y, MARGIN, window.innerHeight - ch - MARGIN),
      };
    }
    return { x: Math.max(MARGIN, window.innerWidth - cw - 16), y: 72 }; // default: top-right
  });
  const posRef = useRef(pos);
  posRef.current = pos;

  // Re-clamp once against the real card size — a position saved on a wide monitor could be
  // off-screen on a smaller one.
  useEffect(() => {
    const cw = cardRef.current?.offsetWidth ?? CARD_W;
    const ch = cardRef.current?.offsetHeight ?? 240;
    setPos((p) => ({
      x: clamp(p.x, MARGIN, window.innerWidth - cw - MARGIN),
      y: clamp(p.y, MARGIN, window.innerHeight - ch - MARGIN),
    }));
  }, []);

  // Drag by the header (mill-portal pattern). Global listeners so the drag continues even if
  // the cursor briefly leaves the small card. Save on END only.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!drag.current) return;
      dragged.current = true;
      const cw = cardRef.current?.offsetWidth ?? CARD_W;
      const ch = cardRef.current?.offsetHeight ?? 240;
      setPos({
        x: clamp(e.clientX - drag.current.dx, MARGIN, window.innerWidth - cw - MARGIN),
        y: clamp(e.clientY - drag.current.dy, MARGIN, window.innerHeight - ch - MARGIN),
      });
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      if (dragged.current) {
        dragged.current = false;
        onSavePosition(posRef.current);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [onSavePosition]);

  const startDrag = (e: React.MouseEvent) => {
    drag.current = { dx: e.clientX - posRef.current.x, dy: e.clientY - posRef.current.y };
  };

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-label="Incoming call"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: CARD_W,
        maxWidth: 'calc(100vw - 16px)',
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        border: `1px solid ${BORDER}`,
        overflow: 'hidden',
        zIndex: 9999,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Header = drag handle. Buttons inside stopPropagation so they stay clickable. */}
      <div
        onMouseDown={startDrag}
        style={{
          cursor: 'grab',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#059669',
          color: 'white',
          padding: '8px 12px',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13 }}>📞 Incoming call</span>
        <span onMouseDown={(e) => e.stopPropagation()}>
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: 18,
              lineHeight: 1,
              cursor: 'pointer',
              padding: '2px 6px',
            }}
          >
            ×
          </button>
        </span>
      </div>

      <div style={{ padding: 12, color: '#111827' }}>
        {!ctx.known ? (
          <>
            <div style={{ fontSize: 12, color: MUTED }}>Unknown caller</div>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>{ctx.from_number}</div>
            <button
              onClick={() => navigate(`/crm/leads/new?phone=${encodeURIComponent(ctx.from_number)}`)}
              style={{
                width: '100%',
                background: '#059669',
                color: 'white',
                border: 'none',
                padding: '9px 12px',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Create Lead
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ctx.name}
                </div>
                <div style={{ fontSize: 11, color: MUTED }}>{ctx.from_number}</div>
              </div>
              {ctx.type_badge && (
                <span
                  style={{
                    flexShrink: 0,
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    background: '#d1fae5',
                    color: '#065f46',
                  }}
                >
                  {ctx.type_badge}
                </span>
              )}
            </div>

            <div style={{ height: 1, background: BORDER, margin: '10px 0' }} />

            {ctx.open_orders && ctx.open_orders.length > 0 ? (
              <div style={{ marginBottom: 4 }}>
                <div style={{ ...label, marginBottom: 2 }}>
                  Open orders{(ctx.open_order_count ?? 0) > 1 ? ` (${ctx.open_order_count})` : ''}
                </div>
                {ctx.open_orders.map((o) => (
                  <div key={o.id} style={row}>
                    <span onClick={() => navigate(`/orders/${o.id}`)} style={linkText}>
                      {o.order_number}
                    </span>
                    <span style={{ color: MUTED, textAlign: 'right' }}>
                      {o.stage}
                      {o.ship_by_date ? ` · ${o.ship_by_date}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ ...row, color: MUTED }}>No open orders</div>
            )}

            <div style={row}>
              <span style={label}>Balance</span>
              <span>
                ${ctx.balance ?? '0.00'}
                {(ctx.open_invoice_count ?? 0) > 0 && (
                  <span style={{ color: MUTED }}>
                    {' '}· {ctx.open_invoice_count} open (${ctx.open_invoice_total})
                  </span>
                )}
              </span>
            </div>

            {ctx.last_completed_order && (
              <div style={row}>
                <span style={label}>Last completed</span>
                <span
                  onClick={() => navigate(`/orders/${ctx.last_completed_order!.id}`)}
                  style={linkText}
                >
                  {ctx.last_completed_order.order_number}
                </span>
              </div>
            )}

            {ctx.bridge_club && (
              <div style={row}>
                <span style={label}>Bridge Club</span>
                <span>
                  {ctx.bridge_club.progress}/10 bags · {ctx.bridge_club.until_reward} to reward
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IncomingCallWatcher;
